import { useIsFocused } from '@react-navigation/native'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  videoId: string
  aspectRatio?: number
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      iframe: React.DetailedHTMLProps<
        React.IframeHTMLAttributes<HTMLIFrameElement>,
        HTMLIFrameElement
      >
    }
  }
}

export default function YoutubePlayer({ videoId, aspectRatio = 16 / 9 }: Props) {
  const [isLoaded, setIsLoaded] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const isFocused = useIsFocused()
  const { t } = useTranslation()
  const embedUrl = `https://www.youtube.com/embed/${videoId}?playsinline=1&enablejsapi=1`

  useEffect(() => {
    if (isFocused) return

    setIsLoaded(false)
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
      '*'
    )
  }, [isFocused])

  useEffect(() => {
    const iframe = iframeRef.current

    return () => {
      iframe?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      )
    }
  }, [])

  return (
    <div className="flex justify-center w-full relative">
      {/* Loading placeholder */}
      {!isLoaded && (
        <div
          className="absolute inset-0 rounded-xl bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ aspectRatio }}
        >
          <div className="text-gray-400 text-center">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-gray-500 rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm">{t('youtubePlayer.loadingVideo')}</p>
          </div>
        </div>
      )}

      {/* YouTube iframe */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        className="rounded-xl"
        style={{
          width: '100%',
          aspectRatio,
          border: 'none',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={() => {
          if (isFocused) {
            setIsLoaded(true)
          }
        }}
      />
    </div>
  )
}
